import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $convertFromMarkdownString } from '@lexical/markdown';
import { $getSelection, $isRangeSelection } from 'lexical';
import { PLAYGROUND_TRANSFORMERS } from './MarkDownTransformers';

const MARKDOWN_PATTERNS = [
  /^#{1,6}\s/, // Headers
  /^\*\*.*\*\*/, // Bold
  /^_.*_/, // Italic
  /^\*.*\*/, // Italic
  /^~~.*~~/, // Strikethrough
  /^`.*`/, // Inline code
  /^```/, // Code blocks
  /^\[.*\]\(.*\)/, // Links
  /^!\[.*\]\(.*\)/, // Images
  /^>\s/, // Blockquotes
  /^[-*+]\s/, // Unordered lists
  /^\d+\.\s/, // Ordered lists
  /^---$/, // Horizontal rules
  /^\|.*\|/, // Tables
];

function isMarkdownContent(text) {
  // Check if the text contains markdown patterns
  const lines = text.split('\n');
  let markdownIndicators = 0;

  // Check for common markdown patterns
  const commonPatterns = [
    /\*\*.*\*\*/, // Bold text anywhere in line
    /_.*_/, // Italic text anywhere in line
    /\*.*\*/, // Italic text anywhere in line
    /~~.*~~/, // Strikethrough anywhere in line
    /`.*`/, // Inline code anywhere in line
    /\[.*\]\(.*\)/, // Links anywhere in line
    /!\[.*\]\(.*\)/, // Images anywhere in line
  ];

  for (const line of lines) {
    const trimmedLine = line.trim();

    // Check line-start patterns
    for (const pattern of MARKDOWN_PATTERNS) {
      if (pattern.test(trimmedLine)) {
        markdownIndicators++;
        break;
      }
    }

    // Check inline patterns
    if (markdownIndicators === 0) {
      for (const pattern of commonPatterns) {
        if (pattern.test(trimmedLine)) {
          markdownIndicators++;
          break;
        }
      }
    }
  }

  // If we have any markdown indicators and the text is substantial, consider it markdown
  return markdownIndicators > 0 && (text.length > 50 || markdownIndicators >= 2);
}

function detectMarkdownFromClipboard(clipboardData) {
  const text = clipboardData.getData('text/plain');
  const html = clipboardData.getData('text/html');
  
  // If there's HTML content, prefer that over markdown conversion
  if (html && html.trim() !== '') {
    return null;
  }
  
  // Check if the text looks like markdown
  if (text && isMarkdownContent(text)) {
    return text;
  }
  
  return null;
}

export default function MarkdownPastePlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const handlePaste = (event) => {
      const clipboardData = event.clipboardData || window.clipboardData;
      const markdownText = detectMarkdownFromClipboard(clipboardData);
      
      if (markdownText) {
        event.preventDefault();
        
        editor.update(() => {
          const selection = $getSelection();
          
          if ($isRangeSelection(selection)) {
            try {
              // Convert markdown to Lexical nodes
              $convertFromMarkdownString(markdownText, PLAYGROUND_TRANSFORMERS);
            } catch (error) {
              console.warn('Failed to parse markdown, falling back to plain text:', error);
              // Fallback to inserting plain text if markdown parsing fails
              selection.insertText(markdownText);
            }
          }
        });
      }
    };

    const rootElement = editor.getRootElement();
    if (rootElement) {
      rootElement.addEventListener('paste', handlePaste);
      
      return () => {
        rootElement.removeEventListener('paste', handlePaste);
      };
    }
  }, [editor]);

  return null;
}
